import { TerminalEventStatus } from '@/model/Agent.model';

import type { AgentSessionStreamService, EventOfType } from './AgentSessionStreamService';
import { subscribeStreamDrops } from './subscribeStreamDrops';

interface SessionStreamCallbacks {
  /** A run on the session reached a terminal event; the session itself may still have more to do. */
  onTerminal: (event: EventOfType<TerminalEventStatus>) => void;
  /** The connection dropped; called once per gap, as with {@link subscribeStreamDrops}. */
  onDrop: () => void;
}

/**
 * Opens a session's stream for a consumer that re-syncs from an authoritative source whenever a run
 * may have finished: on every terminal event, and once per dropped connection. Subscribes before
 * connecting so no event is missed.
 *
 * The stream stays open until the returned `stop()` is called or the optional `signal` aborts; either
 * removes these listeners and disconnects, and `stop()` is safe to call more than once. The caller owns
 * the service, so it can subscribe to other event types (before this call, to be live from the first
 * event) and disconnect early once it knows the session is done.
 */
export const watchSessionStream = (
  service: AgentSessionStreamService,
  { onTerminal, onDrop }: SessionStreamCallbacks,
  { signal }: { signal?: AbortSignal } = {}
): (() => void) => {
  if (signal?.aborted === true) {
    return () => undefined;
  }
  const listeners = new AbortController();
  const stop = (): void => {
    if (!listeners.signal.aborted) {
      listeners.abort();
      service.disconnect();
    }
  };
  Object.values(TerminalEventStatus).forEach((terminal) =>
    service.subscribe(terminal, onTerminal, { signal: listeners.signal })
  );
  subscribeStreamDrops(service, onDrop, { signal: listeners.signal });
  signal?.addEventListener('abort', stop, { signal: listeners.signal });
  service.connect();
  return stop;
};
